import { useQueryClient } from '@tanstack/react-query';
import { RotateCw } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { isApiError } from '../../api/client';
import { useApi } from '../../api/context';
import type { Attachment, Network } from '../../api/types';
import { useToast } from '../../components/toast';
import { ActionButton } from '../../components/ui';
import { strings } from '../../strings';
import { networkErrorInfo, useManageNetworks } from '../shared';
import { useStartNetworkRun } from './hooks';

const p = strings.networks.page;

/**
 * What a failed attachment offers (spec 11 6.15.5 side panel, 6.15.7): Retry (operate; `POST …/connect`, then the run
 * opens), except when its error needs a network apply: then Plan network for network admins and the Ask sentence for
 * others. Nothing for other states.
 */
export function AttachmentRecovery({
  network,
  attachment,
  small,
}: {
  network: Network;
  attachment: Attachment;
  small?: boolean;
}) {
  const api = useApi();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const manage = useManageNetworks();
  const { start, busy: planning } = useStartNetworkRun(network);
  const [busy, setBusy] = useState(false);
  if (attachment.state !== 'failed') return null;
  const info = attachment.lastError
    ? networkErrorInfo(attachment.lastError.code, {
        network: network.name,
        deployment: attachment.deploymentName,
        message: attachment.lastError.message,
      })
    : null;
  if (info?.needsNetworkApply) {
    return manage ? (
      <ActionButton
        permission="manage-networks"
        className={small ? 'btn-sm' : undefined}
        disabled={planning}
        onClick={() => void start({ kind: 'plan' })}
      >
        {strings.networks.actions.planNetwork}
      </ActionButton>
    ) : (
      <span className="text-[12px] text-fg-muted">{strings.networks.askAdminToApply(network.name)}</span>
    );
  }
  const retry = async () => {
    setBusy(true);
    try {
      const run = await api.connectAttachment(network.id, attachment.deploymentId);
      void queryClient.invalidateQueries({ queryKey: ['network', network.id] });
      void queryClient.invalidateQueries({ queryKey: ['deployment', attachment.deploymentId] });
      void queryClient.invalidateQueries({ queryKey: ['runs'] });
      navigate(`/runs/${run.id}`);
    } catch (error) {
      const mapped = isApiError(error)
        ? networkErrorInfo(error.code, {
            network: network.name,
            deployment: attachment.deploymentName,
            message: error.detail,
          })
        : null;
      if (mapped) toast.show({ tone: 'error', title: mapped.sentence });
      else toast.error(error);
      void queryClient.invalidateQueries({ queryKey: ['network', network.id] });
    } finally {
      setBusy(false);
    }
  };
  return (
    <ActionButton
      permission="operate"
      className={small ? 'btn-sm' : undefined}
      disabled={busy}
      icon={<RotateCw aria-hidden className="size-3.5" />}
      aria-label={`${p.retry} ${attachment.deploymentName}`}
      onClick={() => void retry()}
    >
      {p.retry}
    </ActionButton>
  );
}
